import React, { memo } from "react";

import { Box } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { UnifiedCarouselImage } from "@/src/types/carousel";

interface CarouselThumbnailsProps {
  images: UnifiedCarouselImage[];
  currentIndex: number;
  onThumbnailClick?: (index: number) => void;
  showNavigation?: boolean;
}

const CarouselThumbnails = ({
  images,
  currentIndex,
  onThumbnailClick,
  showNavigation = true
}: CarouselThumbnailsProps) => {
  if (!showNavigation || images.length <= 1) return null;

  // Calculate visible thumbnails window
  // Show 6 thumbnails, trying to keep current index in middle
  const thumbnailStartIndex = Math.max(
    0,
    Math.min(currentIndex - 2, images.length - 6)
  );

  const visibleImages = images.slice(
    thumbnailStartIndex,
    thumbnailStartIndex + 6
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        overflowX: "auto",
        overflowY: "hidden",
        py: 1.25,
        px: 0,
        mt: 1,
        flexShrink: 0,
        "&::-webkit-scrollbar": {
          height: "8px"
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.misc.outlineBorder,
          borderRadius: "4px"
        }
      }}
    >
      {visibleImages.map((image, relativeIndex) => {
        const absoluteIndex = thumbnailStartIndex + relativeIndex;
        const isSelected = absoluteIndex === currentIndex;

        return (
          <Box
            key={image.id}
            onClick={() => onThumbnailClick?.(absoluteIndex)}
            sx={{
              flexShrink: 0,
              width: "100px",
              height: "75px",
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
              transition: "all 0.2s ease",
              border: "none",
              "&:hover": {
                opacity: 0.8,
                transform: "scale(1.05)"
              }
            }}
          >
            <Box
              component="img"
              src={image.thumbnailUrl ?? image.url}
              alt={image.alt ?? `Thumbnail ${absoluteIndex + 1}`}
              loading="lazy"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }}
            />
            {isSelected && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: theme.palette.action.disabledBackground,
                  pointerEvents: "none"
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default memo(CarouselThumbnails);
